//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import { maskSensitiveData } from './securityUtils';

// These methods are private, don't export them.
const noSessionStorage = () => {
  console.warn('StorageService: no session storage available in browser!');
};

const noLocalStorage = () => {
  console.warn('StorageService: no local storage available in browser!');
};

const doSetItem = (
  noStorage: () => void,
  getStorage: () => Storage,
  itemName: string,
  itemValue: string,
) => {
  const storage = getStorage();
  if (!storage) {
    noStorage();
    return;
  }

  // To avoid WSOD, always catch if the quota is exceeded.
  try {
    storage.setItem(itemName, itemValue);
  } catch (error) {
    console.error(
      'doSetItem: Unable to store data — key exceeded quotas. (key = %s, size = %i)',
      itemName,
      itemValue?.length ?? 0,
    );
  }
};

const doGetItem = (
  noStorage: () => void,
  getStorage: () => Storage,
  itemName: string,
) => {
  const storage = getStorage();
  if (!storage) {
    noStorage();
    return null;
  }
  return storage.getItem(itemName);
};

const doRemoveItem = (
  noStorage: () => void,
  getStorage: () => Storage,
  itemName: string,
) => {
  const storage = getStorage();
  if (!storage) {
    noStorage();
    return;
  }
  storage.removeItem(itemName);
};

const doClearItems = (noStorage: () => void, getStorage: () => Storage) => {
  const storage = getStorage();
  if (!storage) {
    noStorage();
    return;
  }
  storage.clear();
};

const doSetObject = (
  noStorage: () => void,
  getStorage: () => Storage,
  itemName: string,
  itemObject: object,
) => {
  let jsonOfObject = '{}';
  // don't try to set if object is undefined or null.
  if (!itemObject) {
    return;
  }
  try {
    jsonOfObject = JSON.stringify(itemObject);
  } catch (e) {
    // report error in console but return null.
    console.error(
      'doSetObject: unable to stringify object\nkey:' +
        itemName +
        '\ncontent: "' +
        itemObject.toString() +
        '"\n',
    );
    console.error(e);
  }
  doSetItem(noStorage, getStorage, itemName, jsonOfObject);
};

const doGetObject = (
  noStorage: () => void,
  getStorage: () => Storage,
  itemName: string,
) => {
  const resultObjectString = doGetItem(noStorage, getStorage, itemName);
  let result = null;
  try {
    if (resultObjectString) {
      result = JSON.parse(resultObjectString);
    }
  } catch (e) {
    // report error in console but don't attempt to parse again
    console.error('doGetObject: unable to parse object with key: ' + itemName);
    console.error(e);
  }
  return result;
};

const makeKey = (itemName: string) => {
  return 'devops-automation-' + itemName;
};

// end private methods

/**
 * A class to help store and retrieve data in local and session storage.
 * Local storage persists until explicitly cleared, either programmatically or manually by user.
 * Local storage is tied to the protocol and hostname (e.g. https + devopsautomation.hcl-software.com is different
 * from http + devopsautomation.hcl-software.com)
 * Session storage persists only on the page (tab). When the tab is closed the storage is
 * cleared. The storage persists even if you go to a new page. This means you want to be sure
 * you do not store any kind of sensitive information in it.
 * Each tab has its own session storage (session storage is NOT tied to web/cookie session)
 */
class StorageService {
  /**
   * store a string in session storage
   * @param  {} itemName - name of item to store
   * @param  {} itemValue - string value to store
   */
  setSessionItem(itemName: string, itemValue: string) {
    doSetItem(
      noSessionStorage,
      () => window.sessionStorage,
      itemName,
      itemValue,
    );
  }

  /**
   * @param  {} itemName - name of item to get
   */
  getSessionItem(itemName: string) {
    return doGetItem(noSessionStorage, () => window.sessionStorage, itemName);
  }

  /**
   * store an object in session storage
   * @param  {} itemName - name of item to store
   * @param  {} itemObject - a JavaScript object to store - will be stringified as JSON
   */
  setSessionObject(itemName: string, itemObject: object) {
    return doSetObject(
      noSessionStorage,
      () => window.sessionStorage,
      itemName,
      itemObject,
    );
  }

  /**
   * @param  {} itemName - name of item to get
   */
  getSessionObject(itemName: string) {
    return doGetObject(noSessionStorage, () => window.sessionStorage, itemName);
  }

  /**
   * @param  {} itemName - name of item to remove
   */
  removeSessionItem(itemName: string) {
    doRemoveItem(noSessionStorage, () => window.sessionStorage, itemName);
  }

  /**
   * Clears all session items from storage
   */
  clearSessionItems() {
    doClearItems(noSessionStorage, () => window.sessionStorage);
  }

  /**
   * Remove every sessionStorage entry whose key starts with the given prefix.
   * Used for grouped cleanup (e.g. clearing all per-app iframe URLs on logout
   * so a subsequent user on a shared tab does not inherit the previous user's
   * last-viewed paths). Silently no-ops if sessionStorage is unavailable.
   *
   * @param prefix - key prefix to match (exact, case-sensitive)
   */
  removeSessionItemsWithPrefix(prefix: string) {
    if (!prefix) return;
    try {
      const storage = window.sessionStorage;
      if (!storage) {
        noSessionStorage();
        return;
      }
      const toRemove: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key?.startsWith(prefix)) {
          toRemove.push(key);
        }
      }
      toRemove.forEach((k) => storage.removeItem(k));
    } catch {
      // sessionStorage may throw in private-browsing/quota-exceeded edge cases.
    }
  }

  /**
   * store a string in local storage
   * @param  {} itemName - name of item to store
   * @param  {} itemValue - string value to store
   */
  setLocalItem(itemName: string, itemValue: string) {
    doSetItem(noLocalStorage, () => window.localStorage, itemName, itemValue);
  }

  /**
   * @param  {} itemName - name of item to get
   */
  getLocalItem(itemName: string) {
    return doGetItem(noLocalStorage, () => window.localStorage, itemName);
  }

  /**
   * store an object in local storage
   * @param  {} itemName - name of item to store
   * @param  {} itemObject - a JavaScript object to store - will be stringified as JSON
   */
  setLocalObject(itemName: string, itemObject: object) {
    return doSetObject(
      noLocalStorage,
      () => window.localStorage,
      itemName,
      itemObject,
    );
  }

  /**
   * @param  {} itemName - name of item to get
   */
  getLocalObject(itemName: string) {
    return doGetObject(noLocalStorage, () => window.localStorage, itemName);
  }

  /**
   * @param  {} itemName - name of item to remove
   */
  removeLocalItem(itemName: string) {
    doRemoveItem(noLocalStorage, () => window.localStorage, itemName);
  }

  /**
   * Clear all local storage for this site.
   */
  clearLocalItems() {
    doClearItems(noLocalStorage, () => window.localStorage);
  }

  /**
   * Store an item in local storage using devops-automation prefix
   * @param itemName - name of item to store
   * @param itemValue - value to store
   */
  setLocalItemWithKey(itemName: string, itemValue: string | undefined) {
    const key = makeKey(itemName);
    if (itemValue) {
      this.setLocalItem(key, itemValue);
    }
  }

  /**
   * Retrieves an item in local storage using devops-automation prefix
   * @param itemName - name of item to retrieve from storage
   * @returns - value of item in storage
   */
  getLocalItemWithKey(itemName: string) {
    const key = makeKey(itemName);
    return this.getLocalItem(key);
  }

  removeLocalItemWithKey(itemName: string) {
    const key = makeKey(itemName);
    this.removeLocalItem(key);
  }

  setCurrentTeamspace(
    teamspaceId: string | undefined,
    teamspaceName: string | undefined,
  ) {
    this.setLocalItemWithKey(this.KEYS.TEAMSPACE_ID, teamspaceId);
    this.setLocalItemWithKey(this.KEYS.TEAMSPACE_NAME, teamspaceName);
  }

  // A place to store keys that may be accessed from different components.
  // Normally you could just define the key where it gets used, but if you have items
  // that need to be accessed from different files, you could define the key once
  // here ane reuse it.
  KEYS = {
    SIDEBAR_EXPANDED: 'automation-sidebar-expanded',
    TEAMSPACE_NAME: 'automation-teamspace-name',
    TEAMSPACE_ID: 'automation-teamspace-id',
    BANNER_DISMISSED: 'automation-banner-dismissed',
    // Prefix for per-app last-seen iframe URLs (NEXUS00005880).
    // Full key is built as `${IFRAME_URL_PREFIX}${appName.toLowerCase()}`.
    IFRAME_URL_PREFIX: 'automation-iframe-url:',
    // Active loop context — persisted so it survives new-tab navigations
    // (e.g. "View Resource" opening in _blank) and browser refreshes.
    ACTIVE_LOOP_ID: 'automation-active-loop-id',
    ACTIVE_LOOP_NAME: 'automation-active-loop-name',
    ACTIVE_TEAMSPACE_ID: 'automation-active-teamspace-id',
  };
}

const storageService = new StorageService();
Object.freeze(storageService);
export default storageService;
